/**
 * @file FreeMinutesNotice.tsx
 * @brief The Free plan's minutes on Home and the recorder Setup (W7, B4,
 *        22-10-2026), web and Mac only.
 *
 * @details
 * From `freeMinutes` on GET /billing/subscriptions (null = not on Free, so
 * nothing shows for plans, company accounts or a blocked country):
 * - email not confirmed → "Confirm your email to start recording" with
 *   Resend email and I've confirmed;
 * - used up → the line, "You've used your free minutes this month. Pro has
 *   unlimited Meeting Notes.", the reset day and See Pro;
 * - 80% → the line and a calm "N free minutes left" sentence;
 * - otherwise → the line alone, quiet.
 * Monthly: never "add a card", Free has no overage. One-time (B20 / W17):
 * "220 of 300 free minutes left · No expiry" (or "· Ends 23 Nov"); used up or
 * ended offers See Pro. B43 (31-10-2026): one pool, "300 free minutes for
 * Meeting Notes or Full Intelligence" over the line; used up or a refused
 * grant (device / email) = the sentence + "Pick a plan", never Cards; Full
 * capped by Super Admin and used = "Full Intelligence is on Pro. Pick a
 * plan." under the line. The phone shows none
 * of the Pro words (ADR-043); this file is web only.
 *
 * Pro / Business (B6, 22-10-2026), from `fairUse` (null = no ceiling): the
 * same box, but nothing under 80%; at 80% "Meeting Notes: 48 of 60 hours
 * this month · resets 15 November"; used up adds "You've used this month's
 * 60 hours of Meeting Notes. They reset on 15 November." No card, no
 * upgrade, nothing to buy.
 *
 * Related: ../freeMinutes.ts
 *
 * @author DES
 * @date 22-10-2026
 */

import { Link } from "react-router-dom";
import { Gauge, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { useMySubscription, useResendVerifyEmail } from "../api";
import { billingPath } from "../billingPaths";
import {
  fairUseLine,
  fairUseReachedMessage,
  fairUseState,
  FREE_INTEL_ON_PRO_MESSAGE,
  freeIntelUsedUp,
  freeMinutesLine,
  freeMinutesState,
  freePoolTitle,
  freeResetsMessage,
  freeUsedUpMessage,
  freeWarnMessage,
  isOneTime,
} from "../freeMinutes";

const BOX = "flex items-start gap-3 rounded-2xl px-4 py-3 text-sm";

export function FreeMinutesNotice({ className }: { className?: string }) {
  const status = useMySubscription();
  const resend = useResendVerifyEmail();
  const fm = status.data?.freeMinutes ?? null;
  const state = freeMinutesState(fm);
  const line = freeMinutesLine(fm);
  if (!fm || !state) return <FairUseNotice className={className} />;

  if (state === "verify") {
    return (
      <div className={cn(BOX, "bg-brand/[0.06]", className)} role="status">
        <MailCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden />
        <span className="min-w-0 flex-1 text-app-text">
          <span className="block font-medium">Confirm your email to start recording</span>
          <span className="block text-muted-foreground">
            We've sent you a link. Tap it, then come back here.
          </span>
          <span className="mt-2 flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={resend.isPending}
              onClick={() =>
                resend.mutate(undefined, {
                  onSuccess: (r) =>
                    toast({
                      title: r.emailVerified ? "Email confirmed" : r.sent ? "Email sent" : "Already sent",
                      description: r.emailVerified
                        ? "You can start recording."
                        : r.sent
                        ? "Check your inbox for the link."
                        : "We sent one a few minutes ago. Check your inbox and spam folder.",
                    }),
                  onError: (e) =>
                    toast({
                      variant: "destructive",
                      title: "Could not send",
                      description: e instanceof ApiClientError ? e.message : "Please try again.",
                    }),
                })
              }
            >
              Resend email
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={status.isFetching}
              onClick={() => void status.refetch()}
            >
              I've confirmed
            </Button>
          </span>
        </span>
      </div>
    );
  }

  if (state === "blocked") {
    const resets = freeResetsMessage(fm);
    return (
      <div className={cn(BOX, "bg-muted/60", className)} role="status">
        <Gauge className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1 text-app-text">
          <span className="block font-medium">{line}</span>
          <span className="block text-muted-foreground">
            {fm.grantRefused ? "" : freeUsedUpMessage(fm)}
            {resets ? ` ${resets}` : ""}{" "}
            <Link to={billingPath("plan")} className="font-medium text-brand hover:underline">
              {isOneTime(fm) || fm.grantRefused ? "Pick a plan" : "See Pro"}
            </Link>
          </span>
        </span>
      </div>
    );
  }

  if (state === "warn") {
    return (
      <div className={cn(BOX, "bg-muted/60", className)} role="status">
        <Gauge className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1 text-app-text">
          <span className="block font-medium">{line}</span>
          <span className="block text-muted-foreground">{freeWarnMessage(fm)}</span>
        </span>
      </div>
    );
  }

  const title = freePoolTitle(fm);
  const intelOnPro = freeIntelUsedUp(fm);
  return (
    <div className={cn("text-[13px] text-muted-foreground", className)}>
      {title ? <p className="font-medium text-app-text">{title}</p> : null}
      <p>{line}</p>
      {intelOnPro ? (
        <p>
          {FREE_INTEL_ON_PRO_MESSAGE.replace(/ Pick a plan\.$/, "")}{" "}
          <Link to={billingPath("plan")} className="font-medium text-brand hover:underline">
            Pick a plan
          </Link>
        </p>
      ) : null}
    </div>
  );
}

/** Pro / Business Meeting Notes fair use (B6): only from 80%. */
function FairUseNotice({ className }: { className?: string }) {
  const status = useMySubscription();
  const fu = status.data?.fairUse ?? null;
  const state = fairUseState(fu);
  const line = fairUseLine(fu);
  if (!fu || !state || !line) return null;
  return (
    <div className={cn(BOX, "bg-muted/60", className)} role="status">
      <Gauge className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 flex-1 text-app-text">
        <span className="block font-medium">{line}</span>
        {state === "blocked" ? (
          <span className="block text-muted-foreground">{fairUseReachedMessage(fu)}</span>
        ) : null}
      </span>
    </div>
  );
}
